import { WalletConfig, WalletField } from "@/types/DocumentTemplate";
import {
  IconArrowRight,
  IconBus,
  IconPlane,
  IconShip,
  IconTrain,
} from "@tabler/icons-react";
import {
  BarcodeBlock,
  FieldRow,
  PassCard,
  PassHeader,
  passInsetX,
  useColors,
  emptyValueMark,
  HIDE_ROW,
  useDeviceOnly,
  useRowCertainty,
  useTemplateRenderer,
  useVisibleCounts,
  FitText,
} from "./sharedPreviewBits";
import { TransitType } from "../walletPassTypeSchemas";

const TRANSIT_ICON: Record<TransitType, typeof IconPlane> = {
  air: IconPlane,
  train: IconTrain,
  boat: IconShip,
  bus: IconBus,
  generic: IconArrowRight,
};

type Props = {
  config: WalletConfig;
  currentLanguage: string;
};

export function BoardingPassPreview({ config, currentLanguage }: Props) {
  const colors = useColors(config);
  const deviceOnly = useDeviceOnly();
  const certaintyOfPrimary = useRowCertainty("primaryFields");
  const certaintyOfSecondary = useRowCertainty("secondaryFields");
  const certaintyOfAuxiliary = useRowCertainty("auxiliaryFields");
  
  // The primary value is drawn here rather than through `FieldRow`, so the
  // "resolved to nothing" rule has to be asked for explicitly — this used to
  // be another hand-copy of `deviceOnly ? null : "—"`. `colors.fg`, not
  // `colors.lbl`: this lane has always drawn its dash in the value colour.
  const primaryMark = (field: WalletField | undefined): React.ReactNode => {
    const mark = emptyValueMark({
      deviceOnly,
      certainty: field ? certaintyOfPrimary(field) : undefined,
      labelColor: colors.fg,
    });
    return mark === HIDE_ROW ? null : (mark as React.ReactNode);
  };
  /**
   * Is this slot's row one the delivered pass will NOT carry?
   *
   * The label of a hand-rolled primary slot renders outside `primaryMark`, so
   * suppressing only the VALUE left "PLATZ" standing over nothing — the exact
   * picture this whole change exists to remove, surviving on three pass types.
   * An earlier comment here called that "the closest it can get"; it was not,
   * the label is a `&&` away.
   */
  const primaryGone = (field: WalletField | undefined) =>
    deviceOnly &&
    !!field?.key &&
    certaintyOfPrimary(field) === "absent";
  
  
  const { render, text } = useTemplateRenderer();
  const visible = useVisibleCounts(config);
  // MEMBERSHIP, not just falsiness. The `|| "air"` covers `undefined` and `""`,
  // and the `as TransitType` cast tells tsc to stop asking — but `transitType`
  // is a stored string that reaches this from a saved template, an import or an
  // older schema. Any unrecognised NON-EMPTY value indexes `TRANSIT_ICON` to
  // `undefined`, and `<TransitIcon />` on an undefined component throws and
  // takes the whole builder down. A preview must not be able to crash on data
  // it merely fails to recognise.
  //
  // `hasOwnProperty`, not `in`: `in` walks the prototype chain, so a stored
  // "constructor" or "valueOf" passed the guard and rendered `<Object />` or
  // threw. `transitType` is a client-supplied string with no write-side
  // validation, so those are reachable, not theoretical.
  const raw = config.transitType;
  const transit: TransitType =
    typeof raw === "string" &&
    Object.prototype.hasOwnProperty.call(TRANSIT_ICON, raw)
      ? (raw as TransitType)
      : "air";
  const TransitIcon = TRANSIT_ICON[transit];

  const primary = config.primaryFields || [];
  const origin = primary[0];
  const destination = primary[1];

  return (
    <PassCard bg={colors.bg}>
      <PassHeader
        config={config}
        currentLanguage={currentLanguage}
        colors={colors}
      />

      {/* Origin → Destination.
          The one shared row in any preview that KEEPS `flex-1` after the
          measurement that took it out of `FieldRow`, and the exemption is
          deliberate rather than missed.

          `FieldRow` used to split every two-field row at the card midpoint; a
          device screenshot showed Apple giving the leading field 61% and
          packing the trailing one right, so its cells became content-sized
          (`flex-initial`, trailing `flex-none max-w-[75%]`). That row has
          nothing between its two cells. THIS one has the transit icon, and the
          icon has to sit in the MIDDLE — which is exactly what two equal halves
          around a `shrink-0` glyph produce and what content-sized cells
          destroy: with "Hamburg" and "München" the icon would slide left, and
          with a short origin and a long destination it would sit off-centre by
          a third of the card.

          The cost is honest: an origin longer than half the row clips at the
          midpoint, the very complaint that started the `FieldRow` work. It is
          accepted because there is NO device measurement of a boarding pass —
          Apple's own fills this slot with three-letter airport codes, so the
          contended case may not exist on a real pass at all. Do not "fix" this
          to match `FieldRow` without measuring a boarding pass on a device
          first; symmetry here is a design constraint, not leftover drift. */}
      <div className="pt-2 pb-3 flex items-end justify-between gap-2" style={passInsetX}>
        <div className="flex-1 min-w-0">
          {!primaryGone(origin) && (
            <div
              className="text-[10px] uppercase tracking-wider truncate"
              style={{ color: colors.lbl }}
            >
              {text(origin?.label, currentLanguage)
                ? render(text(origin?.label, currentLanguage))
                : " "}
            </div>
          )}
          {/* Hand-rolled primary — it bypasses `FieldRow`, so `FitText` has to
              be named here or these two keep the old clip-only behaviour while
              every other value on the card shrinks. These four hand-rolled
              lanes have drifted from the shared one before, which is why
              `alignmentAppliesCallSites` exists. */}
          <FitText
            // No `align`: the half above is the default-aligned one, and
            // `FitText` inherits its `text-align` like every other lane.
            className="text-3xl font-bold tracking-tight whitespace-nowrap overflow-hidden"
            style={{ color: colors.fg }}
          >
            {text(origin?.value, currentLanguage)
              ? render(text(origin?.value, currentLanguage))
              : primaryMark(origin)}
          </FitText>
        </div>
        <div
          className="pb-1 shrink-0"
          style={{ color: colors.fg }}
          aria-hidden
        >
          <TransitIcon size={28} />
        </div>
        <div className="flex-1 min-w-0 text-right">
          {!primaryGone(destination) && (
            <div
              className="text-[10px] uppercase tracking-wider truncate"
              style={{ color: colors.lbl }}
            >
              {text(destination?.label, currentLanguage)
                ? render(text(destination?.label, currentLanguage))
                : " "}
            </div>
          )}
          <FitText
            // No `align`: this half is `text-right`, and `FitText` reads that
            // from the cascade — the destination shrinks toward the edge it is
            // aligned to rather than crawling away from it. Spelling it out
            // here was a second source of truth for the same fact.
            className="text-3xl font-bold tracking-tight whitespace-nowrap overflow-hidden"
            style={{ color: colors.fg }}
          >
            {text(destination?.value, currentLanguage)
              ? render(text(destination?.value, currentLanguage))
              : primaryMark(destination)}
          </FitText>
        </div>
      </div>

      {config.secondaryFields && config.secondaryFields.length > 0 && (
        <div className="pb-2" style={passInsetX}>
          <FieldRow
            fields={config.secondaryFields}
            certaintyOf={certaintyOfSecondary}
            currentLanguage={currentLanguage}
            colors={colors}
          visibleCount={visible.secondary}
          />
        </div>
      )}

      {config.auxiliaryFields && config.auxiliaryFields.length > 0 && (
        <div className="pb-2" style={passInsetX}>
          <FieldRow
            fields={config.auxiliaryFields}
            certaintyOf={certaintyOfAuxiliary}
            currentLanguage={currentLanguage}
            colors={colors}
            visibleCount={visible.auxiliary}
          />
        </div>
      )}

      <BarcodeBlock config={config} colors={colors} />
      {/* `description` is metadata (add-sheet title + VoiceOver), never on face. */}
    </PassCard>
  );
}
